import { SHIP_STATS, type ShipType } from './constants';
import type { HexKey } from './hex';
import { asOrdnanceId, asShipId, type OrdnanceId, type ShipId } from './ids';
import type {
  ActionGuards,
  AstrogationOrder,
  C2S,
  CombatAttack,
  FleetPurchase,
  OrbitalBaseEmplacement,
  OrdnanceLaunch,
  OrdnanceType,
  Result,
  TransferOrder,
} from './types';
import type { S2C } from './types/protocol';
import { isObject, isString } from './util';

const isShipType = (value: string): value is ShipType => value in SHIP_STATS;

const ORDNANCE_TYPES = new Set<OrdnanceType>(['mine', 'torpedo', 'nuke']);
const isOrdnanceType = (value: string): value is OrdnanceType =>
  ORDNANCE_TYPES.has(value as OrdnanceType);

const MAX_FLEET_PURCHASES = 64;
const MAX_ASTROGATION_ORDERS = 64;
const MAX_ORDNANCE_LAUNCHES = 64;
const MAX_BASE_EMPLACEMENTS = 32;
const MAX_COMBAT_ATTACKS = 64;
const MAX_ATTACKERS_PER_COMBAT = 16;
const MAX_WEAK_GRAVITY_CHOICES = 64;
const MAX_SURRENDER_SHIPS = 64;
const MAX_TRANSFER_ORDERS = 64;

const isIntegerInRange = (
  value: unknown,
  min: number,
  max: number,
): value is number =>
  Number.isInteger(value) &&
  (value as number) >= min &&
  (value as number) <= max;

const isNullableIntegerInRange = (
  value: unknown,
  min: number,
  max: number,
): value is number | null =>
  value === null || isIntegerInRange(value, min, max);

const parseFleetPurchases = (raw: unknown): FleetPurchase[] | null => {
  if (!Array.isArray(raw) || raw.length > MAX_FLEET_PURCHASES) {
    return null;
  }

  const purchases: FleetPurchase[] = [];

  for (const item of raw) {
    if (!isObject(item)) {
      return null;
    }

    if (item.kind === 'orbitalBaseCargo') {
      purchases.push({ kind: 'orbitalBaseCargo' });
      continue;
    }

    if (
      item.kind === 'ship' &&
      isString(item.shipType) &&
      isShipType(item.shipType) &&
      item.shipType !== 'orbitalBase'
    ) {
      purchases.push({ kind: 'ship', shipType: item.shipType });
      continue;
    }

    // Backward-compatible parsing for older clients/events that sent raw ship types.
    if (isString(item.shipType) && isShipType(item.shipType)) {
      if (item.shipType === 'orbitalBase') {
        purchases.push({ kind: 'orbitalBaseCargo' });
      } else {
        purchases.push({ kind: 'ship', shipType: item.shipType });
      }
      continue;
    }

    return null;
  }

  return purchases;
};

const parseWeakGravityChoices = (
  raw: unknown,
): Record<HexKey, boolean> | undefined | null => {
  if (raw == null) return undefined;

  if (!isObject(raw)) return null;

  const entries = Object.entries(raw);

  if (entries.length > MAX_WEAK_GRAVITY_CHOICES) {
    return null;
  }

  const parsed: Record<HexKey, boolean> = {} as Record<HexKey, boolean>;

  for (const [key, value] of entries) {
    if (typeof value !== 'boolean') return null;
    parsed[key as HexKey] = value;
  }

  return parsed;
};

const parseAstrogationOrders = (raw: unknown): AstrogationOrder[] | null => {
  if (!Array.isArray(raw) || raw.length > MAX_ASTROGATION_ORDERS) {
    return null;
  }

  const orders: AstrogationOrder[] = [];

  for (const item of raw) {
    if (!isObject(item) || !isString(item.shipId) || item.shipId.length === 0) {
      return null;
    }

    if (!isNullableIntegerInRange(item.burn, 0, 5)) {
      return null;
    }

    if (
      item.overload !== undefined &&
      !isNullableIntegerInRange(item.overload, 0, 5)
    ) {
      return null;
    }

    if (item.land !== undefined && typeof item.land !== 'boolean') {
      return null;
    }

    const weakGravityChoices = parseWeakGravityChoices(item.weakGravityChoices);

    if (weakGravityChoices === null) {
      return null;
    }

    orders.push({
      shipId: asShipId(item.shipId),
      burn: item.burn,
      overload: item.overload === undefined ? null : item.overload,
      weakGravityChoices,
      // The explicit orbital-landing intent must survive the validator —
      // dropping it turns a confirmed landing into staying in orbit.
      ...(item.land === true ? { land: true } : {}),
    });
  }

  return orders;
};

const parseOrdnanceLaunches = (raw: unknown): OrdnanceLaunch[] | null => {
  if (!Array.isArray(raw) || raw.length > MAX_ORDNANCE_LAUNCHES) {
    return null;
  }

  const launches: OrdnanceLaunch[] = [];

  for (const item of raw) {
    if (!isObject(item) || !isString(item.shipId) || item.shipId.length === 0) {
      return null;
    }

    if (!isString(item.ordnanceType) || !isOrdnanceType(item.ordnanceType)) {
      return null;
    }

    if (
      item.torpedoAccel !== undefined &&
      !isNullableIntegerInRange(item.torpedoAccel, 0, 5)
    ) {
      return null;
    }

    if (
      item.torpedoAccelSteps !== undefined &&
      item.torpedoAccelSteps !== null &&
      item.torpedoAccelSteps !== 1 &&
      item.torpedoAccelSteps !== 2
    ) {
      return null;
    }

    launches.push({
      shipId: asShipId(item.shipId),
      ordnanceType: item.ordnanceType,
      torpedoAccel: item.torpedoAccel === undefined ? null : item.torpedoAccel,
      torpedoAccelSteps:
        item.torpedoAccelSteps === undefined ? null : item.torpedoAccelSteps,
    });
  }

  return launches;
};

const parseBaseEmplacements = (
  raw: unknown,
): OrbitalBaseEmplacement[] | null => {
  if (!Array.isArray(raw) || raw.length > MAX_BASE_EMPLACEMENTS) {
    return null;
  }

  const emplacements: OrbitalBaseEmplacement[] = [];

  for (const item of raw) {
    if (!isObject(item) || !isString(item.shipId) || item.shipId.length === 0) {
      return null;
    }

    emplacements.push({ shipId: asShipId(item.shipId) });
  }

  return emplacements;
};

const parseCombatTargetId = (
  raw: unknown,
  targetType: 'ship' | 'ordnance',
): ShipId | OrdnanceId | null => {
  if (!isString(raw) || raw.length === 0) {
    return null;
  }

  return targetType === 'ordnance' ? asOrdnanceId(raw) : asShipId(raw);
};

const parseCombatAttacks = (raw: unknown): CombatAttack[] | null => {
  if (!Array.isArray(raw) || raw.length > MAX_COMBAT_ATTACKS) {
    return null;
  }

  const attacks: CombatAttack[] = [];

  for (const item of raw) {
    if (
      !isObject(item) ||
      !Array.isArray(item.attackerIds) ||
      item.targetId === undefined
    ) {
      return null;
    }

    if (
      item.attackerIds.length === 0 ||
      item.attackerIds.length > MAX_ATTACKERS_PER_COMBAT
    ) {
      return null;
    }

    const attackerIds: ShipId[] = [];

    for (const attackerId of item.attackerIds) {
      if (!isString(attackerId) || attackerId.length === 0) {
        return null;
      }

      attackerIds.push(asShipId(attackerId));
    }

    if (
      item.targetType !== undefined &&
      item.targetType !== 'ship' &&
      item.targetType !== 'ordnance'
    ) {
      return null;
    }

    const targetType = item.targetType ?? 'ship';
    const targetId = parseCombatTargetId(item.targetId, targetType);
    if (!targetId) {
      return null;
    }

    const { attackStrength: rawAttackStrength } = item;
    let attackStrength: number | null = null;

    if (rawAttackStrength !== undefined && rawAttackStrength !== null) {
      if (
        typeof rawAttackStrength !== 'number' ||
        !Number.isInteger(rawAttackStrength) ||
        rawAttackStrength < 1 ||
        rawAttackStrength > 99
      ) {
        return null;
      }

      attackStrength = rawAttackStrength;
    }

    attacks.push({
      attackerIds,
      targetId,
      targetType,
      attackStrength,
    });
  }

  return attacks;
};

const parseSingleCombatAttack = (raw: unknown): CombatAttack | null => {
  const attacks = parseCombatAttacks(Array.isArray(raw) ? raw : [raw]);
  return attacks && attacks.length === 1 ? attacks[0] : null;
};

const parseSurrenderShipIds = (raw: unknown): ShipId[] | null => {
  // Support shorthand `{ type: "surrender" }` from some MCP/agent clients.
  // The game server expands an empty list to "all surrenderable own ships".
  if (raw === undefined) {
    return [];
  }

  if (
    !Array.isArray(raw) ||
    raw.length === 0 ||
    raw.length > MAX_SURRENDER_SHIPS
  ) {
    return null;
  }

  const ids: ShipId[] = [];

  for (const item of raw) {
    if (!isString(item) || item.length === 0) return null;
    ids.push(asShipId(item));
  }

  return ids;
};

const parseTransferOrders = (raw: unknown): TransferOrder[] | null => {
  if (!Array.isArray(raw) || raw.length > MAX_TRANSFER_ORDERS) {
    return null;
  }

  const transfers: TransferOrder[] = [];

  for (const item of raw) {
    if (!isObject(item)) return null;

    if (!isString(item.sourceShipId) || item.sourceShipId.length === 0) {
      return null;
    }

    if (!isString(item.targetShipId) || item.targetShipId.length === 0) {
      return null;
    }

    if (
      item.transferType !== 'fuel' &&
      item.transferType !== 'cargo' &&
      item.transferType !== 'passengers'
    ) {
      return null;
    }

    if (
      typeof item.amount !== 'number' ||
      !Number.isInteger(item.amount) ||
      item.amount < 1 ||
      item.amount > 9999
    ) {
      return null;
    }

    transfers.push({
      sourceShipId: asShipId(item.sourceShipId),
      targetShipId: asShipId(item.targetShipId),
      transferType: item.transferType,
      amount: item.amount,
    });
  }

  return transfers;
};

const VALID_PHASES: ReadonlySet<string> = new Set([
  'waiting',
  'fleetBuilding',
  'astrogation',
  'ordnance',
  'logistics',
  'combat',
  'gameOver',
]);

const MAX_IDEMPOTENCY_KEY_LENGTH = 128;

// Parse optional ActionGuards from a wire message. Returns undefined when no
// valid guards are present so the resulting C2S stays clean.
const parseActionGuards = (raw: unknown): ActionGuards | undefined => {
  if (!isObject(raw)) return undefined;
  const guards: ActionGuards = {};

  if (
    typeof raw.expectedTurn === 'number' &&
    Number.isInteger(raw.expectedTurn) &&
    raw.expectedTurn >= 0
  ) {
    guards.expectedTurn = raw.expectedTurn;
  }

  if (
    typeof raw.expectedPhase === 'string' &&
    VALID_PHASES.has(raw.expectedPhase)
  ) {
    guards.expectedPhase = raw.expectedPhase as ActionGuards['expectedPhase'];
  }

  if (
    typeof raw.idempotencyKey === 'string' &&
    raw.idempotencyKey.length > 0 &&
    raw.idempotencyKey.length <= MAX_IDEMPOTENCY_KEY_LENGTH
  ) {
    guards.idempotencyKey = raw.idempotencyKey;
  }

  return Object.keys(guards).length > 0 ? guards : undefined;
};

export const validateClientMessage = (raw: unknown): Result<C2S> => {
  const invalid = (error: string) => ({ ok: false as const, error });

  if (!isObject(raw) || !isString(raw.type)) {
    return invalid('Invalid message payload');
  }

  const message = raw as Record<string, unknown> & {
    type: C2S['type'];
  };

  const guards = parseActionGuards(message.guards);
  const ok = (value: C2S) => ({
    ok: true as const,
    value: guards ? ({ ...value, guards } as C2S) : value,
  });
  const fromParsed = <T>(
    parsed: T | null,
    toValue: (value: T) => C2S,
    error: string,
  ) => (parsed ? ok(toValue(parsed)) : invalid(error));

  const validateChat = () => {
    if (!isString(message.text)) {
      return invalid('Invalid chat payload');
    }

    const text = message.text.trim();

    if (text.length === 0 || text.length > 200) {
      return invalid('Invalid chat payload');
    }

    return ok({ type: 'chat', text });
  };

  const validatePing = () =>
    typeof message.t === 'number' && Number.isFinite(message.t)
      ? ok({ type: 'ping', t: message.t })
      : invalid('Invalid ping payload');

  switch (message.type) {
    case 'fleetReady':
      return fromParsed(
        parseFleetPurchases(message.purchases),
        (purchases) => ({ type: 'fleetReady', purchases }),
        'Invalid fleet payload',
      );

    case 'astrogation':
      return fromParsed(
        parseAstrogationOrders(message.orders),
        (orders) => ({ type: 'astrogation', orders }),
        'Invalid astrogation payload',
      );

    case 'ordnance':
      return fromParsed(
        parseOrdnanceLaunches(message.launches),
        (launches) => ({ type: 'ordnance', launches }),
        'Invalid ordnance payload',
      );

    case 'emplaceBase':
      return fromParsed(
        parseBaseEmplacements(message.emplacements),
        (emplacements) => ({ type: 'emplaceBase', emplacements }),
        'Invalid emplacement payload',
      );

    case 'combat':
      return fromParsed(
        parseCombatAttacks(message.attacks),
        (attacks) => ({ type: 'combat', attacks }),
        'Invalid combat payload',
      );

    case 'combatSingle':
      return fromParsed(
        parseSingleCombatAttack(message.attack),
        (attack) => ({ type: 'combatSingle', attack }),
        'Invalid combat payload',
      );

    case 'surrender':
      return fromParsed(
        parseSurrenderShipIds(message.shipIds),
        (shipIds) => ({ type: 'surrender', shipIds }),
        'Invalid surrender payload',
      );

    case 'logistics':
      return fromParsed(
        parseTransferOrders(message.transfers),
        (transfers) => ({ type: 'logistics', transfers }),
        'Invalid logistics payload',
      );

    case 'skipOrdnance':
    case 'beginCombat':
    case 'skipCombat':
    case 'endCombat':
    case 'skipLogistics':
    case 'rematch':
      return ok({ type: message.type });

    case 'chat':
      return validateChat();

    case 'ping':
      return validatePing();

    default: {
      const _exhaustive: never = message.type;
      return invalid(`Unknown message type: ${_exhaustive}`);
    }
  }
};

// S2C (server-to-client) runtime schema guard.
// Validates structural shape (type + required fields with correct basic types).
// GameState internals are not deeply validated — the server is the authority.

const isPlayerId = (value: unknown): boolean => value === 0 || value === 1;
const isFiniteNumber = (value: unknown): boolean =>
  typeof value === 'number' && Number.isFinite(value);

export const validateServerMessage = (raw: unknown): Result<S2C> => {
  const ok = (value: S2C) => ({ ok: true as const, value });
  const invalid = (error: string) => ({ ok: false as const, error });

  if (!isObject(raw) || !isString(raw.type)) {
    return invalid('Invalid message payload');
  }

  const msg = raw as Record<string, unknown> & { type: S2C['type'] };

  switch (msg.type) {
    case 'welcome':
      if (
        !isPlayerId(msg.playerId) ||
        !isString(msg.code) ||
        !isString(msg.playerToken)
      ) {
        return invalid('Invalid welcome payload');
      }
      return ok(msg as unknown as S2C);

    case 'spectatorWelcome':
      if (!isString(msg.code)) {
        return invalid('Invalid spectatorWelcome payload');
      }
      return ok(msg as unknown as S2C);

    case 'matchFound':
    case 'rematchPending':
      return ok(msg as unknown as S2C);

    case 'gameStart':
      if (!isObject(msg.state)) {
        return invalid('Invalid gameStart payload');
      }
      return ok(msg as unknown as S2C);

    case 'movementResult':
      if (
        !isObject(msg.state) ||
        !Array.isArray(msg.movements) ||
        !Array.isArray(msg.ordnanceMovements) ||
        !Array.isArray(msg.events)
      ) {
        return invalid('Invalid movementResult payload');
      }
      return ok(msg as unknown as S2C);

    case 'combatResult':
      if (!isObject(msg.state) || !Array.isArray(msg.results)) {
        return invalid('Invalid combatResult payload');
      }
      return ok(msg as unknown as S2C);

    case 'combatSingleResult':
      if (!isObject(msg.state) || !isObject(msg.result)) {
        return invalid('Invalid combatSingleResult payload');
      }
      return ok(msg as unknown as S2C);

    case 'stateUpdate':
      if (!isObject(msg.state)) {
        return invalid('Invalid stateUpdate payload');
      }
      return ok(msg as unknown as S2C);

    case 'gameOver':
      if (
        !isPlayerId(msg.winner) ||
        !isString(msg.reason) ||
        (msg.ratingDelta !== undefined &&
          (typeof msg.ratingDelta !== 'number' ||
            !Number.isFinite(msg.ratingDelta)))
      ) {
        return invalid('Invalid gameOver payload');
      }
      return ok(msg as unknown as S2C);

    case 'chat':
      if (!isPlayerId(msg.playerId) || !isString(msg.text)) {
        return invalid('Invalid chat payload');
      }
      return ok(msg as unknown as S2C);

    case 'error':
      if (!isString(msg.message)) {
        return invalid('Invalid error payload');
      }
      return ok(msg as unknown as S2C);

    case 'actionAccepted':
      if (
        msg.guardStatus !== 'inSync' &&
        msg.guardStatus !== 'stalePhaseForgiven'
      ) {
        return invalid('Invalid actionAccepted payload');
      }
      if (
        msg.submitterPlayerId !== undefined &&
        !isPlayerId(msg.submitterPlayerId)
      ) {
        return invalid('Invalid actionAccepted payload');
      }
      if (!isObject(msg.actual) || !isObject(msg.expected)) {
        return invalid('Invalid actionAccepted payload');
      }
      return ok(msg as unknown as S2C);

    case 'actionRejected':
      if (!isString(msg.reason) || !isString(msg.message)) {
        return invalid('Invalid actionRejected payload');
      }
      if (
        msg.submitterPlayerId !== undefined &&
        !isPlayerId(msg.submitterPlayerId)
      ) {
        return invalid('Invalid actionRejected payload');
      }
      if (!isObject(msg.actual) || !isObject(msg.expected)) {
        return invalid('Invalid actionRejected payload');
      }
      if (!isObject(msg.state)) {
        return invalid('Invalid actionRejected payload');
      }
      return ok(msg as unknown as S2C);

    case 'pong':
      if (!isFiniteNumber(msg.t)) {
        return invalid('Invalid pong payload');
      }
      return ok(msg as unknown as S2C);

    case 'opponentStatus':
      if (msg.status !== 'disconnected' && msg.status !== 'reconnected') {
        return invalid('Invalid opponentStatus payload');
      }
      return ok(msg as unknown as S2C);

    default: {
      const _exhaustive: never = msg.type;
      return invalid(`Unknown message type: ${_exhaustive}`);
    }
  }
};
